import {useObvioUser} from 'obvio/auth'
import useDebounce from 'lib/debounce'
import {api} from 'lib/url'
import {teamMemberClient} from 'obvio/obvio-client'
import {Organization} from 'organization'
import React, {useEffect, useState, useCallback} from 'react'
import {sendRequest} from 'obvio/Organizations/CreateOrganizationForm'

interface UserOrganizationsContextProps {
  organizations: Organization[]
  loading: boolean
  searchQuery: string
  setSearchQuery: (value: string) => void
}

const UserOrganizationsContext = React.createContext<
  UserOrganizationsContextProps | undefined
>(undefined)

export default function UserOrganizationsProvider(props: {
  children: React.ReactNode
}) {
  const [searchQuery, setSearchQuery] = useState('')
  const debouncedSearchQuery = useDebounce(searchQuery, 400)
  const [organizations, setOrganizations] = useState<Organization[] | null>(
    null,
  )

  const {
    has_active_subscription: isSubscribed,
    first_name,
    last_name,
  } = useObvioUser()

  const newOrganizationName = `${first_name} ${last_name}'s Organization`

  const createOrganization = useCallback(() => {
    const orgData = {
      name: newOrganizationName,
      logo: null,
    }

    sendRequest(orgData)
      .then((data) => {
        setOrganizations([data])
      })
      .catch((error) => {
        console.error(error)
      })
  }, [newOrganizationName])

  useEffect(() => {
    loadOrganizations(debouncedSearchQuery)
      .then((data) => {
        if (debouncedSearchQuery.trim()) {
          setOrganizations(data)
          return
        }

        // Auto-create an organization if a user is subscribed, and they
        // haven't created on already.
        const shouldAutoCreateOrganization = isSubscribed && data.length === 0
        if (shouldAutoCreateOrganization) {
          createOrganization()
          return
        }

        setOrganizations(data)
      })
      .catch((error) => {
        console.error(error)
      })
  }, [debouncedSearchQuery, createOrganization, isSubscribed])

  return (
    <UserOrganizationsContext.Provider
      value={{
        loading: organizations === null,
        organizations: organizations || [],
        searchQuery,
        setSearchQuery,
      }}
    >
      {props.children}
    </UserOrganizationsContext.Provider>
  )
}

export function useUserOrganizations() {
  const context = React.useContext(UserOrganizationsContext)
  if (context === undefined) {
    throw new Error(
      `useUserOrganizations must be used within an UserOrganizationsProvier`,
    )
  }

  return context
}

function loadOrganizations(searchQuery: string) {
  const trimmedSearchQuery = searchQuery.trim()
  const params = new URLSearchParams()
  if (trimmedSearchQuery) {
    params.set('search', trimmedSearchQuery)
  }

  const queryString = params.toString()
  const path = queryString ? `/organizations?${queryString}` : '/organizations'

  return teamMemberClient.get<Organization[]>(api(path))
}
